import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Stack,
  Card,
  CardContent,
  Chip,
  Avatar,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { People, Person, Edit, Delete, Add, Block, LockOpen, History } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useOwner } from "../../../../context/OwnerContext";
import ownerService from "../../../../services/owner.service";
import userService from "../../../../services/user.service";
import { useRole } from "../../../../context/RoleContext";
import { useAlert } from "../../../../context/AlertContext";
import {
  IUserObject,
  EUserType,
  IEmployeeRequest,
  IEmployeeUpdateRequest,
} from "../../../../services/api.types";
import EmployeeFormDialog from "./components/EmployeeFormDialog";
import DeleteEmployeeDialog from "./components/DeleteEmployeeDialog";
import AvailabilityChip from "../../../../components/AvailabilityChip";

const Employees = () => {
  const { ownerID } = useParams();
  const navigate = useNavigate();
  const { owner, loading, refetch, setError } = useOwner();
  const { t } = useTranslation();
  const { isSuperAdmin } = useRole();
  const { setAlert } = useAlert();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editTarget, setEditTarget] = useState<IUserObject | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<IUserObject | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [blockActionId, setBlockActionId] = useState<string | null>(null);

  const getStatusColor = (isBlocked?: boolean) => {
    return isBlocked ? "error" : "success";
  };

  const handleBlockToggle = async (employee: IUserObject) => {
    setBlockActionId(employee._id);
    try {
      if (employee.isBlocked) {
        await userService.unblockUser(employee._id);
        setAlert("success", t("users.unblocked"));
      } else {
        await userService.blockUser(employee._id);
        setAlert("success", t("users.blocked"));
      }
      await refetch();
    } catch (err: any) {
      setAlert("error", err.message || t("users.actionFailed"));
    } finally {
      setBlockActionId(null);
    }
  };

  const getUserTypeLabel = (userType: string): string => {
    const keys: Record<string, string> = {
      Washer: "employees.typeWasher",
      Administrator: "employees.typeAdministrator",
      Owner: "employees.typeOwner",
    };
    return keys[userType] ? t(keys[userType]) : userType;
  };

  const getUserTypeColor = (userType: string) => {
    switch (userType) {
      case "Washer":
        return "primary";
      case "Administrator":
        return "warning";
      case "Owner":
        return "success";
      default:
        return "default";
    }
  };

  const openCreate = () => {
    setFormMode("create");
    setEditTarget(null);
    setFormDialogOpen(true);
  };

  const openEdit = (employee: IUserObject) => {
    setFormMode("edit");
    setEditTarget(employee);
    setFormDialogOpen(true);
  };

  const openDelete = (employee: IUserObject) => {
    setDeleteTarget(employee);
    setDeleteOpen(true);
  };

  const handleFormSubmit = async (
    data: IEmployeeRequest | IEmployeeUpdateRequest
  ) => {
    setActionLoading(true);
    try {
      if (formMode === "create") {
        await ownerService.createEmployee(ownerID!, data as IEmployeeRequest);
      } else if (editTarget) {
        await ownerService.updateEmployee(
          ownerID!,
          editTarget._id,
          data as IEmployeeUpdateRequest
        );
      }
      await refetch();
      setFormDialogOpen(false);
    } catch (err: any) {
      setError(err.message || t("employees.saveFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setActionLoading(true);
    try {
      await ownerService.deleteEmployee(ownerID!, deleteTarget._id);
      await refetch();
      setDeleteOpen(false);
    } catch (err: any) {
      setError(err.message || t("employees.deleteFailed"));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box className="p-4">
        <Typography>{t("employees.loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box className="p-4">
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}
        >
          {t("employees.title")}
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          {t("employees.addEmployee")}
        </Button>
      </Box>

      {owner?.employees.length === 0 ? (
        <Paper className="p-6">
          <Box className="text-center">
            <People sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t("employees.emptyTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("employees.emptyDesc")}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} className="mb-6">
            <Grid size={{ xs: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent className="text-center">
                  <Typography variant="h4" color="primary">
                    {owner?.employees.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("employees.statTotal")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent className="text-center">
                  <Typography variant="h4" color="success.main">
                    {owner?.employees.filter((emp) => !emp.isBlocked).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("employees.statActive")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent className="text-center">
                  <Typography variant="h4" color="error.main">
                    {owner?.employees.filter((emp) => emp.isBlocked === true).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("employees.statInactive")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <Card sx={{ height: "100%" }}>
                <CardContent className="text-center">
                  <Typography variant="h4" color="primary">
                    {
                      owner?.employees.filter(
                        (emp) => emp.userType === EUserType.Washer
                      ).length
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("employees.statWashers")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Employees Table — desktop / tablet */}
          <Paper sx={{ display: { xs: "none", md: "block" } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("employees.colEmployee")}</TableCell>
                    <TableCell>{t("employees.colContact")}</TableCell>
                    <TableCell>{t("common.type")}</TableCell>
                    <TableCell>{t("employees.colAvailability")}</TableCell>
                    <TableCell>{t("common.status")}</TableCell>
                    <TableCell>{t("common.actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {owner?.employees.map((employee: IUserObject) => (
                    <TableRow key={employee._id}>
                      <TableCell>
                        <Box className="flex items-center gap-3">
                          <Avatar>
                            <Person />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {employee.firstName} {employee.lastName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {t("employees.idLabel", { id: employee._id })}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {employee.country_code} {employee.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getUserTypeLabel(employee.userType)}
                          color={getUserTypeColor(employee.userType) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <AvailabilityChip
                          status={employee.availabilityStatus}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={employee.isBlocked ? t("employees.statusInactive") : t("employees.statusActive")}
                          color={getStatusColor(employee.isBlocked) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box className="flex gap-1">
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/dashboard/manage-owners/${ownerID}/employees/${employee._id}/washing-history`)
                            }
                            color="primary"
                            title={t("washingHistory.viewEmployeeWashes")}
                            aria-label={t("washingHistory.viewEmployeeWashes")}
                          >
                            <History />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => openEdit(employee)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          {isSuperAdmin && (
                            <IconButton
                              size="small"
                              onClick={() => handleBlockToggle(employee)}
                              disabled={blockActionId === employee._id}
                              color={employee.isBlocked ? "success" : "warning"}
                              title={employee.isBlocked ? t("userDetail.unblock") : t("userDetail.block")}
                            >
                              {employee.isBlocked ? <LockOpen /> : <Block />}
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => openDelete(employee)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Employees list — mobile */}
          <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" } }}>
            {owner?.employees.map((employee: IUserObject) => (
              <Card key={employee._id} variant="outlined">
                <CardContent>
                  <Box className="flex items-center gap-3" sx={{ mb: 1.5 }}>
                    <Avatar>
                      <Person />
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} noWrap>
                        {employee.firstName} {employee.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {employee.country_code} {employee.phone}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}
                  >
                    <Chip
                      label={getUserTypeLabel(employee.userType)}
                      color={getUserTypeColor(employee.userType) as any}
                      size="small"
                    />
                    <AvailabilityChip status={employee.availabilityStatus} />
                    <Chip
                      label={
                        employee.isBlocked
                          ? t("employees.statusInactive")
                          : t("employees.statusActive")
                      }
                      color={getStatusColor(employee.isBlocked) as any}
                      size="small"
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.5,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/dashboard/manage-owners/${ownerID}/employees/${employee._id}/washing-history`)
                      }
                      color="primary"
                      title={t("washingHistory.viewEmployeeWashes")}
                      aria-label={t("washingHistory.viewEmployeeWashes")}
                    >
                      <History />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => openEdit(employee)}
                      color="primary"
                    >
                      <Edit />
                    </IconButton>
                    {isSuperAdmin && (
                      <IconButton
                        size="small"
                        onClick={() => handleBlockToggle(employee)}
                        disabled={blockActionId === employee._id}
                        color={employee.isBlocked ? "success" : "warning"}
                        title={employee.isBlocked ? t("userDetail.unblock") : t("userDetail.block")}
                      >
                        {employee.isBlocked ? <LockOpen /> : <Block />}
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => openDelete(employee)}
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </>
      )}

      <EmployeeFormDialog
        open={formDialogOpen}
        mode={formMode}
        initial={editTarget}
        loading={actionLoading}
        onSubmit={handleFormSubmit}
        onCancel={() => setFormDialogOpen(false)}
      />
      <DeleteEmployeeDialog
        open={deleteOpen}
        employee={deleteTarget}
        loading={actionLoading}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default Employees;
